import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { nameSorter } from '../../../utils';
import useApi from '../../../hooks/useApi';
import RemoveIcon from '../../../../public/assets/images/editor/delete-icon-black.svg';
import DropDown from '../../DropDown';
import ContentEditor from '../../ContentEditor';
import {
  Glossary,
  GlossaryHeader,
  GlossaryBody,
  GlossaryRow,
  GlossaryConcept,
  GlossaryDescription,
} from '../../NodeContentPreview/styles';
import { ContentSection, DeleteConcept, ButtonPanelWrapper, AddButton, LegendSelector } from '../styles';

const GlossaryContentEditor = ({
  children,
  userToken,
  data,
  payload,
  setPayload,
  getUpdates,
  type,
  fileService,
  loading = false,
  updating = false,
}) => {
  const isUpdating = loading || updating;
  const useAutoSave = window.editor.useAutoSave;
  const correlationListId = data?.correlation_list_id || 0;
  const isCorrelationQuiz = type === 'correlation';
  const isLegend = (listId) => data?.legends?.find((legend) => legend.id === listId);

  const mapConcepts = (concepts) =>
    (concepts || []).map((concept) => ({
      ...concept,
      description: { ...concept.CorrelationDescription, ...concept.Description },
    }));

  const [concepts, setConcepts] = useState(mapConcepts(data?.CorrelationList?.Concepts));
  const [activeLegendId, setActiveLegendId] = useState(correlationListId);
  const [defaultId, setDefaultId] = useState(isLegend(correlationListId) ? 0 : correlationListId);
  const [editable, setEditable] = useState(!isCorrelationQuiz);

  const { getData, loading: legendLoading } = useApi(userToken);

  const getLegendData = async () => {
    if (!legendLoading) {
      const response = await getData(`/editor/behaviors/legend_content/${activeLegendId}`);
      if (response.success) {
        setConcepts(mapConcepts(response?.body?.correlation.CorrelationList.CorrelationConcepts));
      }
    }
  };

  useEffect(() => {
    if (!isUpdating) {
      setEditable(!isCorrelationQuiz || !isLegend(activeLegendId));
      const newPayload = { ...payload };
      delete newPayload['concepts'];
      delete newPayload['deletions'];
      if (!isLegend(activeLegendId)) {
        delete newPayload['legend_list_id'];
        setPayload({ ...newPayload, id: data.id, immediateSave: false });
      } else if (activeLegendId) {
        getLegendData();
        setPayload({ ...newPayload, legend_list_id: activeLegendId, immediateSave: false });
      }
    }
  }, [activeLegendId]);

  const addConcept = () => {
    if (isUpdating) {
      return;
    }
    const newConcepts = [...concepts];
    newConcepts.push({ body: '', order: newConcepts.length, description: { body: '' } });
    setConcepts(newConcepts);
  };

  const removeConcept = (index) => {
    if (isUpdating) {
      return;
    }
    const newConcepts = concepts
      .filter((concept) => concept.order !== index)
      .map((concept, index) => ({ ...concept, order: index }));
    setConcepts(newConcepts);
  };

  useEffect(() => {
    const { itemsAddedOrRemoved, itemsUpdated, deletedIds } = getUpdates(
      mapConcepts(data?.CorrelationList?.Concepts),
      concepts,
    );
    const activeIds = deletedIds.filter((id) =>
      data.CorrelationList.Concepts.find(
        (concept) => concept.id == id && concept.correlation_list_id === activeLegendId,
      ),
    );
    if (!isUpdating && (itemsAddedOrRemoved || itemsUpdated) && editable) {
      setPayload({ ...payload, concepts, deletions: { concepts: activeIds }, immediateSave: itemsAddedOrRemoved });
    }
  }, [JSON.stringify(concepts)]);

  const getMissingId = (concepts = []) => {
    let result = -1;
    concepts.forEach((concept, index) => {
      if (!concept.id) {
        result = index;
      }
    });
    return result;
  };

  useEffect(() => {
    setEditable(!isCorrelationQuiz || !isLegend(correlationListId));
    setPayload({ ...payload, concepts, legend_list_id: data.correlation_list_id });
  }, []);

  useEffect(() => {
    if (!isUpdating) {
      setConcepts([]);
    }
  }, [activeLegendId]);

  useEffect(() => {
    const index = getMissingId(concepts);
    const newConcepts = [...concepts];
    if (index !== -1 && newConcepts[index]) {
      newConcepts[index] = {
        ...data.CorrelationList.Concepts[index],
        description: { ...data.CorrelationList.Concepts[index]?.Description },
        order: index,
      };
      setConcepts(newConcepts);
    }
  }, [JSON.stringify(data.CorrelationList.Concepts)]);

  const onChange = (value, editorType, index) => {
    const newConcepts = [...concepts];
    if (editorType === 'description') {
      newConcepts[index].description.body = value;
    } else {
      newConcepts[index].body = value;
    }
    setConcepts(newConcepts);
  };

  const isDisabled = (id) => useAutoSave && !id;

  const getEditor = (editorType, index, value, id) => (
    <ContentEditor
      id={`${editorType}_editor_${index}`}
      autoFocus={editorType === 'concept'}
      type={type}
      aria-label={i18n.__('default_texteditor_placeholder')}
      placeholder={i18n.__('default_texteditor_placeholder')}
      value={value || ''}
      onChange={(value) => !loading && onChange(value, editorType, index)}
      fileService={fileService}
      canToggleToolbar={false}
      showToolbar={true}
      loading={loading}
      disabled={!editable || loading || isDisabled(id)}
      focus={!loading && !isDisabled(id) && editorType === 'concept'}
      theme="bubble"
      toolbar={[['bold', 'italic', 'underline'], ['image']]}
      className={editorType}
    />
  );

  const newLegend = [{ value: defaultId, label: i18n.__('legend_make_new_list') }];
  const legends = newLegend.concat(
    (data?.legends || [])
      .map(({ id, name }) => ({
        value: id,
        label: name || '',
      }))
      .sort(nameSorter),
  );

  return (
    <ContentSection>
      {children}
      {isCorrelationQuiz && (
        <LegendSelector>
          <DropDown
            id="legends"
            items={legends}
            selectedValue={activeLegendId}
            onChange={(ev) => setActiveLegendId(ev.target.value)}
            lightMode={true}
            disabled={loading || legendLoading}
          />
        </LegendSelector>
      )}
      <Glossary>
        <GlossaryHeader>
          <GlossaryConcept>{i18n.__('correlation_term')}</GlossaryConcept>
          <GlossaryDescription>{i18n.__('correlation_description')}</GlossaryDescription>
        </GlossaryHeader>
        <GlossaryBody>
          {(concepts || []).map((concept, index) => (
            <GlossaryRow key={`concept_${index}`} even={index % 2}>
              <GlossaryConcept>{getEditor('concept', index, concept.body, concept.id)}</GlossaryConcept>
              <GlossaryDescription>
                {getEditor('description', index, concept.description.body, concept.id)}
              </GlossaryDescription>
              {editable && (
                <DeleteConcept
                  src={RemoveIcon}
                  title={i18n.__('app_delete')}
                  onClick={() => removeConcept(index)}
                  disabled={loading}
                />
              )}
            </GlossaryRow>
          ))}
        </GlossaryBody>
        {editable && (
          <ButtonPanelWrapper>
            <AddButton
              onClick={addConcept}
              disabled={isUpdating}
              title={i18n.__('activity_add_term')}
              className="add-term-button"
            >
              {i18n.__('activity_add_term')}
            </AddButton>
          </ButtonPanelWrapper>
        )}
      </Glossary>
    </ContentSection>
  );
};

export default GlossaryContentEditor;

GlossaryContentEditor.propTypes = {
  children: PropTypes.object,
  userToken: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  payload: PropTypes.object.isRequired,
  setPayload: PropTypes.func.isRequired,
  getUpdates: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  fileService: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  updating: PropTypes.bool,
};
